import React from 'react';
import Icon from '../../../components/AppIcon';

const InvoiceSummaryPanel = ({ invoices }) => {
  const calculateSummary = () => {
    const summary = {
      total: invoices.length,
      totalAmount: 0,
      paidAmount: 0,
      pendingAmount: 0,
      overdueAmount: 0,
      overdueCount: 0,
      thisMonth: 0,
      thisMonthAmount: 0
    };

    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();

    invoices.forEach(invoice => {
      summary.totalAmount += invoice.amount;
      
      if (invoice.status === 'paid') {
        summary.paidAmount += invoice.amount;
      } else if (invoice.status === 'overdue') {
        summary.overdueAmount += invoice.amount;
        summary.overdueCount++;
      } else if (invoice.status !== 'cancelled') {
        summary.pendingAmount += invoice.amount;
      }

      const invoiceDate = new Date(invoice.date);
      if (invoiceDate.getMonth() === currentMonth && invoiceDate.getFullYear() === currentYear) {
        summary.thisMonth++;
        summary.thisMonthAmount += invoice.amount;
      }
    });

    return summary;
  };

  const summary = calculateSummary();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const summaryCards = [
    {
      title: 'Total Facturas',
      value: summary.total.toString(),
      subtitle: formatCurrency(summary.totalAmount),
      icon: 'FileText',
      color: 'text-primary',
      bgColor: 'bg-primary-50'
    },
    {
      title: 'Facturas Pagadas',
      value: formatCurrency(summary.paidAmount),
      subtitle: `${Math.round((summary.paidAmount / summary.totalAmount) * 100) || 0}% del total`,
      icon: 'CheckCircle',
      color: 'text-success',
      bgColor: 'bg-success-50'
    },
    {
      title: 'Pendientes de Pago',
      value: formatCurrency(summary.pendingAmount),
      subtitle: `${summary.total - summary.overdueCount - invoices.filter(i => i.status === 'paid').length} facturas`,
      icon: 'Clock',
      color: 'text-warning',
      bgColor: 'bg-warning-50'
    },
    {
      title: 'Facturas Vencidas',
      value: formatCurrency(summary.overdueAmount),
      subtitle: `${summary.overdueCount} facturas vencidas`,
      icon: 'AlertCircle',
      color: 'text-error',
      bgColor: 'bg-error-50'
    },
    {
      title: 'Este Mes',
      value: summary.thisMonth.toString(),
      subtitle: formatCurrency(summary.thisMonthAmount),
      icon: 'Calendar',
      color: 'text-accent',
      bgColor: 'bg-accent-50'
    }
  ];

  const getPaymentStatusData = () => {
    const paid = invoices.filter(i => i.status === 'paid').length;
    const pending = invoices.filter(i => i.status === 'sent').length;
    const overdue = invoices.filter(i => i.status === 'overdue').length;
    const draft = invoices.filter(i => i.status === 'draft').length;

    return [
      { label: 'Pagadas', value: paid, color: 'bg-success', percentage: (paid / summary.total) * 100 },
      { label: 'Pendientes', value: pending, color: 'bg-warning', percentage: (pending / summary.total) * 100 },
      { label: 'Vencidas', value: overdue, color: 'bg-error', percentage: (overdue / summary.total) * 100 },
      { label: 'Borradores', value: draft, color: 'bg-secondary', percentage: (draft / summary.total) * 100 }
    ];
  };

  const paymentStatusData = getPaymentStatusData();

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="space-y-4">
        {summaryCards.map((card, index) => (
          <div key={index} className="bg-surface rounded-lg border border-border p-4">
            <div className="flex items-center justify-between mb-3">
              <div className={`p-2 rounded-lg ${card.bgColor}`}>
                <Icon name={card.icon} size={20} className={card.color} />
              </div>
            </div>
            
            <div className="space-y-1">
              <h3 className="text-sm font-body-medium text-text-secondary">
                {card.title}
              </h3>
              <div className="text-xl font-heading-semibold text-text-primary">
                {card.value}
              </div>
              <div className="text-sm text-text-muted">
                {card.subtitle}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Payment Status Chart */}
      <div className="bg-surface rounded-lg border border-border p-4">
        <div className="flex items-center space-x-2 mb-4">
          <Icon name="PieChart" size={20} className="text-text-secondary" />
          <h3 className="text-lg font-heading-semibold text-text-primary">
            Estado de Pagos
          </h3>
        </div>

        <div className="space-y-3">
          {paymentStatusData.map((item, index) => (
            <div key={index} className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${item.color}`}></div>
                  <span className="text-sm font-body-medium text-text-primary">
                    {item.label}
                  </span>
                </div>
                <span className="text-sm text-text-secondary">
                  {item.value} ({Math.round(item.percentage)}%)
                </span>
              </div>
              
              <div className="w-full bg-secondary-100 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${item.color}`}
                  style={{ width: `${item.percentage}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-surface rounded-lg border border-border p-4">
        <div className="flex items-center space-x-2 mb-4">
          <Icon name="Zap" size={20} className="text-text-secondary" />
          <h3 className="text-lg font-heading-semibold text-text-primary">
            Acciones Rápidas
          </h3>
        </div>

        <div className="space-y-3">
          <button className="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-secondary-50 transition-micro text-left">
            <div className="p-2 bg-primary-50 rounded-lg">
              <Icon name="Plus" size={16} className="text-primary" />
            </div>
            <div>
              <div className="text-sm font-body-medium text-text-primary">
                Nueva Factura
              </div>
              <div className="text-xs text-text-secondary">
                Crear factura desde cero
              </div>
            </div>
          </button>

          <button className="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-secondary-50 transition-micro text-left">
            <div className="p-2 bg-accent-50 rounded-lg">
              <Icon name="FileText" size={16} className="text-accent" />
            </div>
            <div>
              <div className="text-sm font-body-medium text-text-primary">
                Desde Presupuesto
              </div>
              <div className="text-xs text-text-secondary">
                Convertir presupuesto aprobado
              </div>
            </div>
          </button>

          <button className="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-secondary-50 transition-micro text-left">
            <div className="p-2 bg-warning-50 rounded-lg">
              <Icon name="Send" size={16} className="text-warning" />
            </div>
            <div>
              <div className="text-sm font-body-medium text-text-primary">
                Enviar Recordatorios
              </div>
              <div className="text-xs text-text-secondary">
                A facturas vencidas
              </div>
            </div>
          </button>

          <button className="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-secondary-50 transition-micro text-left">
            <div className="p-2 bg-success-50 rounded-lg">
              <Icon name="Download" size={16} className="text-success" />
            </div>
            <div>
              <div className="text-sm font-body-medium text-text-primary">
                Exportar Datos
              </div>
              <div className="text-xs text-text-secondary">
                Descargar informe mensual
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-surface rounded-lg border border-border p-4">
        <div className="flex items-center space-x-2 mb-4">
          <Icon name="Activity" size={20} className="text-text-secondary" />
          <h3 className="text-lg font-heading-semibold text-text-primary">
            Actividad Reciente
          </h3>
        </div>

        <div className="space-y-3">
          <div className="flex items-start space-x-3">
            <div className="p-1 bg-success-50 rounded-full">
              <Icon name="CheckCircle" size={12} className="text-success" />
            </div>
            <div className="flex-1">
              <div className="text-sm text-text-primary">
                Factura <span className="font-body-medium">INV-2024-156</span> pagada
              </div>
              <div className="text-xs text-text-secondary">Hace 2 horas</div>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="p-1 bg-primary-50 rounded-full">
              <Icon name="Send" size={12} className="text-primary" />
            </div>
            <div className="flex-1">
              <div className="text-sm text-text-primary">
                Factura <span className="font-body-medium">INV-2024-157</span> enviada
              </div>
              <div className="text-xs text-text-secondary">Hace 4 horas</div>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="p-1 bg-accent-50 rounded-full">
              <Icon name="Plus" size={12} className="text-accent" />
            </div>
            <div className="flex-1">
              <div className="text-sm text-text-primary">
                Nueva factura <span className="font-body-medium">INV-2024-158</span> creada
              </div>
              <div className="text-xs text-text-secondary">Ayer</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceSummaryPanel;